import React from 'react';
import { createOptionCategoryItem, deleteOptionCategory } from '../../apis/backend';
import { sortItemsPositionForShop, setCopyKey } from '../../utils/product';
import { ERROR_DELETE_OPTION, ERROR_DUPLICATION, ERROR_SYNCHRONIZATION_WITH_SHOP } from '../../constants';
import { isObjectNotEmpty, keyGenerator } from '../../utils/tools';
import _ from 'lodash';
import { getShopsToSynchronise } from '../../utils/shopUtils';
import { checkAccess } from 'src/utils/admins';
import { Option } from '../../models/Menu';
import { OptionListProps, OptionListState, ModalData, DragEndEvent, DragStartEvent } from './OptionList.types';
import { Dispatch, SetStateAction } from 'react';

type SetStateFunction = Dispatch<SetStateAction<OptionListState>>;

export const showDeleteOptionModal = (
  option: Option,
  props: OptionListProps,
  stateRef: React.MutableRefObject<OptionListState>,
  setState: SetStateFunction
): void => {
  const { showModal, shopId, shops, nbShops, onActionComplete } = props;
  const nbShopsToSync = _.size(getShopsToSynchronise());

  const modalData: ModalData = {
    color: 'danger',
    title: `Êtes-vous sûr(e) de vouloir supprimer ${option.name} ?`,
    showCancelBtn: true,
    checkboxes: nbShops > 1 && nbShopsToSync > 1 && checkAccess('features', 'globalProduct') ?
      [{
        text: 'Supprimer cette option sur un ou plusieurs magasins',
        onChange: (checked: boolean) => {
          setState(prev => ({ ...prev, synchronize: checked }));
        }
      }] : [],
    btnColor: 'danger',
    onClick: () => {
      const currentState = stateRef.current;
      const shouldSync = currentState.synchronize === true && nbShops > 1 && nbShopsToSync > 1;

      if (shouldSync) {
        setState(prev => ({
          ...prev,
          selectedShops: { [shopId]: shops[shopId] },
          showModal: true,
          option
        }));
      } else {
        handleRemoveOption(option, props, currentState, setState, onActionComplete);
      }

      showModal({
        ...modalData,
        loading: true
      });

      if (!currentState.synchronize) {
        setState(prev => ({ ...prev, synchronize: false, showModal: false }));
      }
    },
    btnTxt: 'Supprimer',
    handleClose: () => {
      showModal(null);
      setState(prev => ({ ...prev, synchronize: false, showModal: false }));
    }
  };
  showModal(modalData);
};

export const handleRemoveOption = async (
  option: Option,
  props: OptionListProps,
  state: OptionListState,
  setState: SetStateFunction,
  onActionComplete?: () => void
): Promise<void> => {
  const { categoryKey, openErrorModal, showModal } = props;
  const { selectedShops, synchronize } = state;
  try {
    showModal({
      color: 'danger',
      title: `Êtes-vous sûr(e) de vouloir supprimer ${option.name} ?`,
      btnColor: 'danger',
      onClick: () => {},
      btnTxt: 'Supprimer',
      loading: true
    });
    if (!synchronize || (synchronize && _.size(selectedShops) > 1)) {
      setState(prev => ({ ...prev, isLoading: true }));
      await deleteOptionCategory(categoryKey, synchronize, selectedShops, option.key);
      setState(prev => ({
        ...prev,
        option: {} as Option,
        showModal: false,
        selectedShops: {},
        isLoading: false
      }));
      showModal(null);
      if (onActionComplete) {
        onActionComplete();
      }
    } else {
      openErrorModal(ERROR_SYNCHRONIZATION_WITH_SHOP);
    }
  } catch (error) {
    openErrorModal(ERROR_DELETE_OPTION);
    setState(prev => ({ ...prev, showModal: false }));
  }
  setState(prev => ({ ...prev, isLoading: false }));
  showModal(null);
};

export const handleDuplicateOption = async (
  option: Option,
  props: OptionListProps,
  state: OptionListState,
  setState: SetStateFunction,
  onActionComplete?: () => void
): Promise<void> => {
  const { openErrorModal, categoryKey } = props;
  try {
    const key = setCopyKey(keyGenerator('ITEM'));
    const { name, position, price, ref } = option;
    const newItem: Option = {
      ...option,
      name,
      position,
      price,
      ref,
      key
    };
    await createOptionCategoryItem(categoryKey, newItem);
    if (onActionComplete) {
      onActionComplete();
    }
  } catch (error) {
    openErrorModal(ERROR_DUPLICATION);
  }
  setState(prev => ({ ...prev, selectedShops: {} }));
  return;
};

export const handleOpenModal = (
  option: Option | Record<string, never>,
  setState: SetStateFunction
): void => {
  setState(prev => ({
    ...prev,
    showOptionModal: true,
    createOption: !isObjectNotEmpty(option),
    option: option as Option
  }));
};

export const closeModal = (setState: SetStateFunction): void => {
  setState(prev => ({
    ...prev,
    showOptionModal: false,
    option: {} as Option,
    synchronize: false
  }));
};

export const handleDragOption = async (
  event: DragEndEvent,
  options: Record<string, Option>,
  categoryKey: string,
  setState: SetStateFunction,
  shopId: string
): Promise<void> => {
  const { active, over } = event;
  if (over && active.id !== over.id) {
    setState(prev => ({ ...prev, isSorting: true }));
    await sortItemsPositionForShop(event, options, categoryKey, true, false, shopId);
    setState(prev => ({
      ...prev,
      isSorting: false,
      activeId: null
    }));
  }
};

export const handleStartDrag = (
  event: DragStartEvent,
  setState: SetStateFunction
): void => {
  setState(prev => ({ ...prev, activeId: String(event.active.id) }));
};

